import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/redux';

import {
  fetchGoogleReviewSettings,
  saveGoogleReviewSettings,
} from '../store/slices/googleReviewSettingsSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { usePermissions } from '../hooks/usePermissions';
import type { GoogleBusinessModel, GoogleReviewAiSettingsView, GoogleToneOfVoice } from '../types/googleReview';
import ArrowBack from '../components/ArrowBack';

const BUSINESS_MODELS: { value: GoogleBusinessModel; labelKey: string }[] = [
  { value: 'offline', labelKey: 'google_review_settings_business_model_offline' },
  { value: 'online',  labelKey: 'google_review_settings_business_model_online' },
  { value: 'hybrid',  labelKey: 'google_review_settings_business_model_hybrid' },
];

const TONES: { value: GoogleToneOfVoice; labelKey: string }[] = [
  { value: 'friendly',     labelKey: 'google_review_settings_tone_friendly' },
  { value: 'professional', labelKey: 'google_review_settings_tone_professional' },
  { value: 'casual',       labelKey: 'google_review_settings_tone_casual' },
  { value: 'empathetic',   labelKey: 'google_review_settings_tone_empathetic' },
  { value: 'formal',       labelKey: 'google_review_settings_tone_formal' },
];

const EMPTY_FORM = {
  businessType: '',
  businessCategory: '',
  businessModel: 'offline' as GoogleBusinessModel,
  toneOfVoice: 'friendly' as GoogleToneOfVoice,
  alwaysInclude: '',
  neverInclude: '',
  brandWordingGuidelines: '',
  supportContactGuidelines: '',
  escalationRules: '',
  additionalRules: '',
  exampleGoodReply: '',
};

const GoogleReviewSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { settings, isLoading, isSaving, error } = useAppSelector(
    (s) => s.googleReviewSettings,
  );

  // Subuser: changing settings requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');

  const [form, setForm] = useState(EMPTY_FORM);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Load on mount
  useEffect(() => {
    dispatch(fetchGoogleReviewSettings());
  }, [dispatch]);

  // Populate form when settings arrive
  useEffect(() => {
    if (settings) {
      setForm({
        businessType: settings.businessType ?? '',
        businessCategory: settings.businessCategory ?? '',
        businessModel: (settings.businessModel ?? 'offline') as GoogleBusinessModel,
        toneOfVoice: (settings.toneOfVoice ?? 'friendly') as GoogleToneOfVoice,
        alwaysInclude: settings.alwaysInclude ?? '',
        neverInclude: settings.neverInclude ?? '',
        brandWordingGuidelines: settings.brandWordingGuidelines ?? '',
        supportContactGuidelines: settings.supportContactGuidelines ?? '',
        escalationRules: settings.escalationRules ?? '',
        additionalRules: settings.additionalRules ?? '',
        exampleGoodReply: settings.exampleGoodReply ?? '',
      });
    }
  }, [settings]);

  // Show error notification
  useEffect(() => {
    if (error) {
      dispatch(addNotification({ type: 'error', message: error }));
    }
  }, [error, dispatch]);

  const handleText = (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(saveGoogleReviewSettings(form as GoogleReviewAiSettingsView));
    if (saveGoogleReviewSettings.fulfilled.match(result)) {
      dispatch(addNotification({ type: 'success', message: t('google_review_settings_saved') }));
    }
  };

  const pageHeader = (
    <div className="google-review-settings-page__header">
      <ArrowBack />
      <h1 className="google-review-settings-page__title">{t('google_review_settings_title')}</h1>
    </div>
  );

  if (isLoading && !settings) {
    return (
      <div className="google-review-settings-page">
        {pageHeader}
        <div className="loading-spinner-wrapper">
          <div className="loading-spinner" aria-label={t('loading_text')} />
        </div>
      </div>
    );
  }

  return (
    <div className="google-review-settings-page">
      {pageHeader}
      <form className="google-review-settings-page__form" onSubmit={handleSubmit}>

        {/* ── Section 1: Business Context ── */}
        <section className="google-review-settings-section">
          <h2 className="google-review-settings-section__heading">{t('google_review_settings_section_business_context')}</h2>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_business_type')}</label>
            <input
              className="google-review-settings-field__input"
              type="text"
              value={form.businessType}
              onChange={handleText('businessType')}
              placeholder={t('google_review_settings_business_type_placeholder')}
            />
          </div>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_business_category')}</label>
            <input
              className="google-review-settings-field__input"
              type="text"
              value={form.businessCategory}
              onChange={handleText('businessCategory')}
              placeholder={t('google_review_settings_business_category_placeholder')}
            />
          </div>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_business_model')}</label>
            <div className="google-review-settings-pill-group" role="group" aria-label={t('google_review_settings_business_model')}>
              {BUSINESS_MODELS.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  className={`google-review-settings-pill${form.businessModel === value ? ' google-review-settings-pill--active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, businessModel: value }))}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_tone')}</label>
            <div className="google-review-settings-pill-group" role="group" aria-label={t('google_review_settings_tone')}>
              {TONES.map(({ value, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  className={`google-review-settings-pill${form.toneOfVoice === value ? ' google-review-settings-pill--active' : ''}`}
                  onClick={() => setForm((f) => ({ ...f, toneOfVoice: value }))}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 2: Core Reply Rules ── */}
        <section className="google-review-settings-section">
          <h2 className="google-review-settings-section__heading">{t('google_review_settings_section_core_rules')}</h2>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_always_include')}</label>
            <textarea
              className="google-review-settings-field__textarea"
              rows={3}
              value={form.alwaysInclude}
              onChange={handleText('alwaysInclude')}
              placeholder={t('google_review_settings_always_include_placeholder')}
            />
          </div>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_never_include')}</label>
            <textarea
              className="google-review-settings-field__textarea"
              rows={3}
              value={form.neverInclude}
              onChange={handleText('neverInclude')}
              placeholder={t('google_review_settings_never_include_placeholder')}
            />
          </div>
        </section>

        {/* ── Section 3: Advanced Guidance (collapsible) ── */}
        <section className="google-review-settings-section">
          <button
            type="button"
            className="google-review-settings-section__toggle"
            aria-expanded={advancedOpen}
            onClick={() => setAdvancedOpen((v) => !v)}
          >
            <span>{t('google_review_settings_section_advanced')}</span>
            <span className={`google-review-settings-section__toggle-icon${advancedOpen ? ' google-review-settings-section__toggle-icon--open' : ''}`} aria-hidden="true">▾</span>
          </button>

          {advancedOpen && (
            <div className="google-review-settings-section__body">
              <div className="google-review-settings-field">
                <label className="google-review-settings-field__label">{t('google_review_settings_brand_wording')}</label>
                <textarea
                  className="google-review-settings-field__textarea"
                  rows={3}
                  value={form.brandWordingGuidelines}
                  onChange={handleText('brandWordingGuidelines')}
                  placeholder={t('google_review_settings_brand_wording_placeholder')}
                />
              </div>

              <div className="google-review-settings-field">
                <label className="google-review-settings-field__label">{t('google_review_settings_support_contact')}</label>
                <textarea
                  className="google-review-settings-field__textarea"
                  rows={3}
                  value={form.supportContactGuidelines}
                  onChange={handleText('supportContactGuidelines')}
                  placeholder={t('google_review_settings_support_contact_placeholder')}
                />
              </div>

              <div className="google-review-settings-field">
                <label className="google-review-settings-field__label">{t('google_review_settings_escalation_rules')}</label>
                <textarea
                  className="google-review-settings-field__textarea"
                  rows={3}
                  value={form.escalationRules}
                  onChange={handleText('escalationRules')}
                  placeholder={t('google_review_settings_escalation_rules_placeholder')}
                />
              </div>

              <div className="google-review-settings-field">
                <label className="google-review-settings-field__label">{t('google_review_settings_additional_rules')}</label>
                <textarea
                  className="google-review-settings-field__textarea"
                  rows={3}
                  value={form.additionalRules}
                  onChange={handleText('additionalRules')}
                  placeholder={t('google_review_settings_additional_rules_placeholder')}
                />
              </div>
            </div>
          )}
        </section>

        {/* ── Section 4: Examples ── */}
        <section className="google-review-settings-section">
          <h2 className="google-review-settings-section__heading">{t('google_review_settings_section_examples')}</h2>

          <div className="google-review-settings-field">
            <label className="google-review-settings-field__label">{t('google_review_settings_example_good_reply')}</label>
            <textarea
              className="google-review-settings-field__textarea"
              rows={4}
              value={form.exampleGoodReply}
              onChange={handleText('exampleGoodReply')}
              placeholder={t('google_review_settings_example_placeholder')}
            />
          </div>
        </section>

        <div className="google-review-settings-page__footer">
          <button
            type="submit"
            className="btn btn-primary google-review-settings-page__save-btn"
            disabled={isSaving || !canWrite}
            title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
          >
            {isSaving ? t('google_review_settings_saving') : t('google_review_settings_save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GoogleReviewSettingsPage;
